import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAuth, Auth, setPersistence, browserLocalPersistence } from 'firebase/auth';

interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
}

let firebaseApp: FirebaseApp | null = null;
let firebaseAuth: Auth | null = null;

export const initializeFirebase = async (): Promise<{ app: FirebaseApp; auth: Auth }> => {
  if (firebaseApp && firebaseAuth) {
    return { app: firebaseApp, auth: firebaseAuth };
  }

  try {
    // Fetch Firebase config from API
    const response = await fetch('/api/auth/firebase-config');
    if (!response.ok) {
      throw new Error('Failed to fetch Firebase config');
    }
    
    const config: FirebaseConfig = await response.json();
    
    // Initialize Firebase
    firebaseApp = initializeApp(config);
    firebaseAuth = getAuth(firebaseApp);
    
    // Set persistence to LOCAL to ensure auth state persists across page reloads
    try {
      await setPersistence(firebaseAuth, browserLocalPersistence);
      console.log('Firebase auth persistence set to LOCAL');
    } catch (error) {
      console.warn('Failed to set Firebase auth persistence:', error);
    }
    
    return { app: firebaseApp, auth: firebaseAuth };
  } catch (error) {
    console.error('Error initializing Firebase:', error);
    throw error;
  }
};

export const getFirebaseApp = (): FirebaseApp => {
  if (!firebaseApp) {
    throw new Error('Firebase not initialized. Call initializeFirebase() first.');
  }
  return firebaseApp;
};

export const getFirebaseAuth = (): Auth => {
  if (!firebaseAuth) {
    throw new Error('Firebase not initialized. Call initializeFirebase() first.');
  }
  return firebaseAuth;
};

export const isFirebaseInitialized = (): boolean => {
  return !!firebaseApp && !!firebaseAuth;
};
